/**
 * QueryState — renders loading / error / empty affordances for an RTK Query
 * result, and only shows children when data is ready. Keeps the screens free of
 * repetitive branch boilerplate. Uses the kit `.empty-state` / `.btn` classes.
 */
import type { ReactNode } from "react";
import { useLocale } from "@hooks/useLocale";

export interface QueryStateProps {
  isLoading: boolean;
  isError: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  onRetry?: () => void;
  children: ReactNode;
}

export function QueryState({
  isLoading,
  isError,
  isEmpty,
  emptyMessage,
  onRetry,
  children,
}: QueryStateProps) {
  const { t } = useLocale();
  if (isLoading) return <div className="empty-state">{t("common.loading")}</div>;
  if (isError)
    return (
      <div className="empty-state">
        {t("common.error")}
        {onRetry ? (
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn" onClick={onRetry}>
              {t("common.retry")}
            </button>
          </div>
        ) : null}
      </div>
    );
  if (isEmpty) return <div className="empty-state">{emptyMessage ?? t("finder.empty")}</div>;
  return <>{children}</>;
}
